import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { LayoutDashboard, BookOpen, Users, Palette, LogOut } from "lucide-react";
import { toast } from "sonner";

const navItems = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/admin/courses", label: "Courses", icon: BookOpen, testid: "nav-courses" },
  { to: "/admin/candidates", label: "Candidates", icon: Users, testid: "nav-candidates" },
  { to: "/admin/branding", label: "Branding", icon: Palette, testid: "nav-branding" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { logoSrc, branding } = useBranding();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 border-r border-border bg-background flex-col">
        <div className="h-20 border-b border-border flex items-center gap-3 px-5">
          {logoSrc ? (
            <img src={logoSrc} alt={branding?.company_name || "Logo"} className="h-14 object-contain" />
          ) : (
            <div className="h-6 w-6 bg-foreground" />
          )}
          <div className="leading-tight">
            <div className="font-mono text-[11px] tracking-tight">EARLY YEARS</div>
            <div className="font-mono text-[11px] tracking-tight">ACADEMY</div>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                  isActive
                    ? "bg-foreground text-background"
                    : "text-foreground hover:bg-secondary"
                }`
              }
            >
              <it.icon size={16} strokeWidth={1.5} />
              <span className="font-medium">{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-4">
          <div className="px-3 py-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Signed in</div>
            <div className="text-sm mt-1 truncate" data-testid="admin-email-display">{user?.email}</div>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="mt-2 w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm border border-border hover:bg-secondary transition-colors"
          >
            <LogOut size={14} strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 h-14 border-b border-border bg-background flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          {logoSrc ? (
            <img src={logoSrc} alt="" className="h-8 object-contain" />
          ) : (
            <div className="h-5 w-5 bg-foreground" />
          )}
          <span className="font-mono text-xs tracking-tight">EARLY YEARS ACADEMY</span>
        </div>
        <button onClick={onLogout} className="font-mono text-xs">Sign out</button>
      </div>
      <div className="md:hidden h-14" />

      {/* Mobile nav bottom */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-border bg-background grid grid-cols-4">
        {navItems.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-2 gap-1 text-[10px] ${
                isActive ? "text-foreground font-semibold" : "text-muted-foreground"
              }`
            }
          >
            <it.icon size={18} strokeWidth={1.5} />
            <span>{it.label}</span>
          </NavLink>
        ))}
      </nav>

      <main className="flex-1 min-w-0 pb-20 md:pb-0">
        <Outlet />
      </main>
    </div>
  );
}
